import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDecorations,
  getLessons,
  getLessonsClass,
  mapStaticElements,
  mapStaticRooms,
} from 'utils/helpers/mapRenderers';
import { useHistory } from 'react-router-dom';
import { IRootState } from 'store';
import { hideLiteracyModal, showLiteracyModal } from 'actions/app-state';
import {
  defaultMapTemplate,
  defaultMapType,
  minHeight,
  SVG_VIEWBOX_WIDTH,
} from 'utils/constants/map';
import StaticMapElements from 'components/common/staticMapElements';
import useWindowSize from 'utils/hooks/useWindowSize';
import { IEvents } from 'types';
import MobileService from 'services/mobile';

let literacyModalIssuer: string | undefined;

interface MapComponentProps {
  lessons: IEvents[];
}

const MapComponent: React.FC<MapComponentProps> = ({ lessons }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const scholar = useSelector((state: IRootState) => state.scholars.scholar);
  const activeScholarId = useSelector(
    (state: IRootState) => state.users.userData?.activeScholarId
  );
  const appState = useSelector((state: IRootState) => state.appState);

  const mapType = scholar?.settings?.mapType || defaultMapType;
  const mapTemplate = scholar?.settings?.mapTemplate || defaultMapTemplate;
  const LessonsViewList = getLessons(
    lessons,
    mapType,
    mapTemplate,
    activeScholarId
  );
  const elementsCount = LessonsViewList.length;

  const literacyModalResult = appState?.literacyModalResult;

  const openLiteracyModal = useCallback(
    (url: string) => {
      literacyModalIssuer = url;
      dispatch(showLiteracyModal());
    },
    [dispatch]
  );

  useEffect(() => {
    if (literacyModalResult && literacyModalIssuer) {
      dispatch(hideLiteracyModal());

      // Override redirect to /process-steps-admin on mobile since there is no such page
      if (
        literacyModalIssuer === '/process-steps-admin' &&
        MobileService.isMobile
      ) {
        literacyModalIssuer = '/dashboard/scholars';
      }

      history.push(literacyModalIssuer);
      literacyModalIssuer = undefined;
    }
  }, [literacyModalResult, history, dispatch]);

  const StaticRooms = mapStaticRooms[mapTemplate];
  const StaticElements = mapStaticElements[mapTemplate];

  const ws = useWindowSize();
  const height = useMemo(() => {
    const svgHeightRate = SVG_VIEWBOX_WIDTH / window.innerWidth;
    const h = (ws.height || window.innerHeight) * svgHeightRate;
    return Math.ceil(!!ws.height && h > minHeight ? h : minHeight);
  }, [ws.height]);

  return (
    <div className="map-wrap">
      <svg
        className={`map-body ${mapTemplate} ${mapType || ''}`}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox={`0 0 ${SVG_VIEWBOX_WIDTH} ${height}`}
      >
        <StaticMapElements
          scholar={scholar}
          staticElements={<StaticElements />}
        />
        <g className="prefix__trope-deco-container road-map">
          {getDecorations(mapTemplate, mapType, elementsCount)}
        </g>
        <g className={getLessonsClass(elementsCount)}>
          <g className="lessons">{LessonsViewList}</g>
        </g>
        <StaticRooms literacyCheck={openLiteracyModal} />
      </svg>
    </div>
  );
};

export default MapComponent;
